import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Upload, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import type { ImportResult } from '../types/master-data.types';
import { textoDelCsv } from '@/lib/utils/decodificar-csv';
import { Aviso } from '@/components/ui/aviso';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityName: string;
  templateHeaders: string[];
  templateRows: string[][];
  helpText?: string;
  importFn: (file: File) => Promise<ImportResult>;
  onSuccess: () => void;
}

export function MasterDataImportDialog({
  open,
  onOpenChange,
  entityName,
  templateHeaders,
  templateRows,
  helpText,
  importFn,
  onSuccess,
}: ImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string[][]>([]);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = () => {
    const rows = [templateHeaders, ...templateRows];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plantilla_${entityName.toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (f: File | null) => {
    setFile(f);
    setResult(null);
    if (!f) {
      setPreview([]);
      setPreviewHeaders([]);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = textoDelCsv(e.target?.result as ArrayBuffer);
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length > 0) {
        setPreviewHeaders(lines[0].split(',').map((h) => h.trim()));
        setPreview(lines.slice(1, 6).map((l) => l.split(',')));
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await importFn(file);
      setResult(res);
      if (res.errors.length === 0) {
        onSuccess();
      }
    } catch {
      // error handled
    }
    setLoading(false);
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setPreviewHeaders([]);
    setResult(null);
    onOpenChange(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.name.endsWith('.csv')) {
      handleFileChange(dropped);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        aria-describedby={undefined}
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Importar {entityName} desde CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Plantilla */}
          <div className="border rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium">1. Descarga la plantilla</p>
            <p className="text-xs text-muted-foreground">
              La plantilla incluye todos los campos necesarios con filas de ejemplo.
            </p>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Descargar plantilla CSV
            </Button>
          </div>

          {/* Help text */}
          {helpText && (
            <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-800 dark:text-blue-300 whitespace-pre-line">
                {helpText}
              </p>
            </div>
          )}

          {/* Upload */}
          <div className="border rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium">2. Sube tu archivo CSV</p>
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => inputRef.current?.click()}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              {file ? (
                <p className="text-sm font-medium text-foreground">{file.name}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Arrastra tu CSV aquí o haz clic para seleccionar
                </p>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </div>

            {/* Preview */}
            {preview.length > 0 && (
              <div className="overflow-x-auto">
                <p className="text-xs text-muted-foreground mb-2">
                  Vista previa (primeras 5 filas):
                </p>
                <table className="w-full text-xs border">
                  <thead>
                    <tr className="bg-muted">
                      {previewHeaders.map((h, i) => (
                        <th
                          key={i}
                          className="px-2 py-1 text-left border-r last:border-r-0 whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, ri) => (
                      <tr key={ri} className="border-t">
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className="px-2 py-1 border-r last:border-r-0 whitespace-nowrap"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Import button */}
          <Button onClick={handleImport} disabled={!file || loading} className="w-full">
            <Upload className="w-4 h-4 mr-2" />
            {loading ? 'Importando...' : 'Importar'}
          </Button>

          {/* Results */}
          {result && (
            <div className="border rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">Resultado de la importación</p>
              {/* 7C-2: los avisos del decodificador (un CSV mezclado) llegan hasta
                  acá: mismo `Aviso` que el asistente de carga del catálogo. */}
              {result.avisos && result.avisos.length > 0 && (
                <Aviso
                  tono="alerta"
                  titulo={result.avisos.length === 1 ? '1 aviso' : `${result.avisos.length} avisos`}
                >
                  <ul className="mt-1 max-h-40 overflow-y-auto text-xs">
                    {result.avisos.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </Aviso>
              )}
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  {result.created} creados, {result.updated} actualizados
                </span>
                {result.errors.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {result.errors.length} errores
                  </span>
                )}
              </div>
              {result.errors.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-2 py-1 text-left border-r">Fila</th>
                        <th className="px-2 py-1 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 border-r">{err.row}</td>
                          <td className="px-2 py-1 text-red-600">{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
